import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Target, Coins, Loader2, CheckCircle2, X, Gift, Flame } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const QUEST_ICONS = {
  explore: "🧭",
  battle_win: "⚔️",
  boss_win: "👑",
  artwork: "🎨",
  shop_buy: "🛒",
  level_up: "⭐",
  gold_spent: "💰",
};

/**
 * Renders the Daily Quests badge button and the full-screen modal.
 * Works both from GameView HUD (compact button) and as standalone component.
 */
export default function DailyQuests() {
  const { game, loadGame } = useGame();
  const { token, API } = useAuth();
  const lang = (game?.language || "en").toLowerCase();
  const isIt = lang.startsWith("it");

  const [open, setOpen] = useState(false);
  const [quests, setQuests] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(null);

  const loadQuests = useCallback(async () => {
    if (!game?.id || !token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/game/${game.id}/daily-quests`, { headers: { Authorization: `Bearer ${token}` } });
      setQuests(r.data.quests || []);
      setStreak(r.data.streak || null);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [API, token, game?.id]);

  // Load quests on mount & when game changes
  useEffect(() => { loadQuests(); }, [loadQuests]);

  // Refresh whenever story pages count or battles stats change (external actions)
  useEffect(() => {
    if (open) loadQuests();
  }, [open, game?.story_pages?.length, game?.stats?.battles_won, loadQuests]);

  const handleClaim = async (questId) => {
    setClaiming(questId);
    try {
      const r = await axios.post(`${API}/game/${game.id}/daily-quests/${questId}/claim`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isIt ? `+${r.data.reward_gold} oro ricompensa!` : `+${r.data.reward_gold} gold rewarded!`);
      if (r.data.streak_bonus) {
        const sb = r.data.streak_bonus;
        toast.success(
          isIt
            ? `🔥 Streak giorno ${sb.streak_count}! Bonus +${sb.bonus_gold} oro!`
            : `🔥 Day ${sb.streak_count} streak! +${sb.bonus_gold} bonus gold!`,
          { duration: 5000 }
        );
      }
      await loadQuests();
      await loadGame(game.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Riscossione fallita" : "Claim failed"));
    } finally {
      setClaiming(null);
    }
  };

  const completedUnclaimed = quests.filter(q => q.completed && !q.claimed).length;

  return (
    <>
      {/* HUD Button */}
      <button
        data-testid="daily-quests-button"
        onClick={() => setOpen(true)}
        className="relative flex items-center gap-1 text-[#94a3b8] hover:text-[#cfb53b] transition-colors"
        title={isIt ? "Missioni Giornaliere" : "Daily Quests"}
      >
        <Target size={14} />
        <span className="text-xs hidden md:inline">{isIt ? "Missioni" : "Quests"}</span>
        {completedUnclaimed > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] bg-[#cfb53b] text-[#0a0a0c] rounded-full text-[9px] font-heading flex items-center justify-center px-1 animate-pulse" data-testid="daily-quests-badge">
            {completedUnclaimed}
          </span>
        )}
      </button>

      {/* Modal */}
      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-3 overflow-y-auto" onClick={() => setOpen(false)} data-testid="daily-quests-modal">
          <div className="bg-[#1a1a1d] border border-[#cfb53b]/30 rounded-lg p-5 max-w-lg w-full my-auto max-h-[calc(100dvh-1.5rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-base text-[#cfb53b] flex items-center gap-2">
                <Target size={16} />
                {isIt ? "Missioni Giornaliere" : "Daily Quests"}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-7 w-7 p-0 text-[#94a3b8]"><X size={14} /></Button>
            </div>

            {/* Streak Banner */}
            {streak && (
              <div className={`mb-4 rounded-lg p-3 border ${streak.bonus_claimed_today ? 'border-orange-500/40 bg-gradient-to-r from-orange-500/10 to-red-500/10' : streak.count > 0 ? 'border-[#cfb53b]/30 bg-[#cfb53b]/5' : 'border-[#2d3748] bg-[#0a0a0c]/40'}`} data-testid="streak-banner">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Flame size={18} className={streak.count > 0 ? "text-orange-400" : "text-[#64748b]"} />
                    <div>
                      <p className="text-xs font-heading text-[#e2e8f0]">
                        {isIt ? "Streak: " : "Streak: "}
                        <span className={streak.count > 0 ? "text-orange-400" : "text-[#94a3b8]"}>{streak.count} {isIt ? "giorni" : "days"}</span>
                      </p>
                      <p className="text-[10px] text-[#64748b]">
                        {streak.bonus_claimed_today
                          ? (isIt ? "✓ Bonus odierno riscosso — continua domani per salire!" : "✓ Today's bonus claimed — keep it up tomorrow!")
                          : (isIt ? `Completa tutte le missioni per +${streak.next_bonus_gold} oro bonus` : `Complete all quests for +${streak.next_bonus_gold} bonus gold`)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1 justify-end">
                      <Coins size={12} className="text-[#cfb53b]" />
                      <span className="text-sm text-[#cfb53b] font-heading" data-testid="streak-next-bonus">+{streak.next_bonus_gold}</span>
                    </div>
                    <p className="text-[9px] text-[#64748b]">{isIt ? "prossimo" : "next"}</p>
                  </div>
                </div>
              </div>
            )}
            <p className="text-[11px] text-[#64748b] mb-4">
              {isIt ? "Completa le missioni per guadagnare oro bonus. Si rigenerano ogni giorno a mezzanotte (UTC)." : "Complete quests to earn bonus gold. They refresh daily at midnight (UTC)."}
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-[#cfb53b]" size={28} /></div>
            ) : quests.length === 0 ? (
              <p className="text-center text-sm text-[#64748b] py-8">{isIt ? "Nessuna missione attiva" : "No active quests"}</p>
            ) : (
              <div className="space-y-3">
                {quests.map(q => {
                  const title = isIt ? q.title_it : q.title_en;
                  const desc = isIt ? q.desc_it : q.desc_en;
                  const pct = Math.min(100, Math.round((q.progress / q.target) * 100));
                  const barColor = q.claimed ? "bg-[#64748b]/50" : q.completed ? "bg-green-500" : "bg-[#cfb53b]";
                  return (
                    <div key={q.id} className={`border rounded-lg p-3 transition-all ${q.claimed ? 'border-[#2d3748] opacity-60' : q.completed ? 'border-green-500/40 bg-green-500/5' : 'border-[#2d3748] bg-[#0a0a0c]/40'}`} data-testid={`quest-${q.type}`}>
                      <div className="flex items-start gap-3">
                        <div className="text-2xl shrink-0">{QUEST_ICONS[q.type] || "🎯"}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="font-heading text-sm text-[#e2e8f0]">{title}</h4>
                            <div className="flex items-center gap-1 shrink-0">
                              <Coins size={12} className="text-[#cfb53b]" />
                              <span className="text-xs text-[#cfb53b] font-heading">{q.reward_gold}</span>
                            </div>
                          </div>
                          <p className="text-[11px] text-[#94a3b8] mt-0.5">{desc}</p>
                          <div className="mt-2">
                            <div className="h-1.5 rounded-full bg-[#2d3748] overflow-hidden">
                              <div className={`h-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-[10px] text-[#64748b]">{q.progress}/{q.target}</span>
                              {q.claimed ? (
                                <span className="text-[10px] text-[#64748b] flex items-center gap-1"><CheckCircle2 size={10} /> {isIt ? "Riscossa" : "Claimed"}</span>
                              ) : q.completed ? (
                                <Button size="sm" onClick={() => handleClaim(q.id)} disabled={claiming === q.id} className="h-6 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] text-[10px] px-2 font-heading" data-testid={`claim-${q.type}`}>
                                  {claiming === q.id ? <Loader2 size={10} className="animate-spin" /> : <><Gift size={10} className="mr-1" /> {isIt ? "Riscuoti" : "Claim"}</>}
                                </Button>
                              ) : (
                                <span className="text-[10px] text-[#64748b]">{isIt ? "In corso" : "In progress"}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
